import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LANGUAGES = ['en', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];

const en = {
  brand: 'SL Meetings',
  nav: { meetings: 'Meetings', new: 'New meeting', upload: 'Upload', vocabulary: 'Vocabulary' },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Retry',
    signOut: 'Sign out',
    none: '—',
    language: 'العربية',
    optional: 'optional',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Shahira Lasheen — meeting records',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    failed: 'Wrong email or password',
  },
  meetings: {
    title: 'Meetings',
    empty: 'No meetings yet.',
    search: 'Search title, client, location',
    filterType: 'Type',
    filterStatus: 'Status',
    from: 'From',
    to: 'To',
    all: 'All',
    duration: 'Duration',
    created: 'Created by',
  },
  types: {
    CLIENT: 'Client',
    SUPPLIER: 'Supplier',
    INTERNAL: 'Internal',
    MANAGEMENT: 'Management',
    ONLINE: 'Online',
    GENERIC: 'General',
  },
  statuses: {
    RECORDING: 'Recording',
    UPLOADED: 'Uploaded',
    TRANSCRIBING: 'Transcribing',
    DRAFTING: 'Drafting',
    IN_REVIEW: 'In review',
    FINAL: 'Final',
    ARCHIVED: 'Archived',
    FAILED: 'Failed',
    NO_SPEECH: 'No speech detected',
  },
  newMeeting: {
    title: 'New meeting',
    meetingTitle: 'Title',
    type: 'Meeting type',
    department: 'Department',
    clientRef: 'Client reference',
    leadRef: 'Lead reference',
    location: 'Location',
    language: 'Meeting language',
    attendees: 'Attendees',
    addAttendee: 'Add attendee',
    attendeeName: 'Name',
    attendeeRole: 'Role in meeting',
    create: 'Create and record',
    createUpload: 'Create and upload a file',
    uploadSourceLabel: 'Upload source',
    uploadSourceFile: 'File',
    uploadSourceZoom: 'Zoom',
  },
  recorder: {
    title: 'Recorder',
    start: 'Start recording',
    stop: 'Stop and process',
    live: 'Recording',
    idle: 'Ready',
    uploaded: 'Chunks uploaded',
    pending: 'Waiting to upload',
    failed: 'Failed chunks',
    retrying: 'Still retrying',
    stalled: 'A chunk is taking a long time to upload. It is still being retried — keep this page open.',
    captureLost: 'The microphone stopped sending audio. Check the device, then stop and finish.',
    recorderError: 'The recorder stopped unexpectedly.',
    stopFailed: 'Not finished',
    stopFailedHelp:
      'The recording is safe on the server. Finish the upload again — do not start a new recording.',
    retryFinish: 'Retry finishing',
    resume: 'Resume upload',
    resumeAvailable: 'An unfinished recording with {{count}} chunk(s) is stored on this device.',
    offline: 'Offline — chunks are queued and will upload when the connection returns.',
    micDenied: 'Microphone permission was refused.',
    unsupported: 'This browser cannot record audio.',
    wakeLock: 'Screen kept awake',
    finishing: 'Finishing upload…',
    uploadingPiece: 'Uploading piece {{current}} of {{total}}',
    lastError: 'Last error',
    done: 'Recording handed to processing.',
    keepOpen: 'Keep this page open until the upload finishes.',
    micCheck: 'Mic check',
    micLabel: 'Mic: {{label}}',
    micLabelUnknown: 'Phone microphone',
    micLevelAria: 'Microphone input level',
    startMicCheck: 'Start mic check',
    interruptedBody:
      'An earlier recording for this meeting was interrupted before Stop was pressed ({{count}} pieces, about {{seconds}} seconds, are already on the server). Start over to discard it and record again, or finish with what was uploaded.',
    interruptedStartOver: 'Start over',
    interruptedFinish: 'Finish with what was uploaded',
    micUnavailable:
      'The microphone could not be opened. Allow microphone access for this site in the browser settings, make sure no other app is using it, then reload this page.',
    selectMic: 'Microphone',
    silenceWarning:
      'No sound is reaching the microphone. Check that the phone is not connected to another device as a microphone, and that nothing else is using the mic.',
  },
  upload: {
    title: 'Upload a recording',
    pick: 'Choose an audio or video file',
    drop: 'Drop a file here, or tap to choose',
    submit: 'Upload',
    uploading: 'Uploading',
    done: 'Uploaded. Transcription has been queued.',
    tooBig: 'That file is larger than 500 MB.',
    importTitle: 'Import an existing transcript',
    importIntro:
      'Paste a WebVTT (.vtt) transcript, a plain-text transcript, or a transcript JSON file, and skip transcription entirely.',
    importPasteLabel: 'Paste transcript text',
    importPastePlaceholder: 'WEBVTT… or [00:00:00] Speaker: text… or JSON',
    importFileLabel: 'Or choose a file (.vtt, .txt, .json)',
    importProviderLabel: 'Source',
    importProviderZoom: 'Zoom',
    importProviderOther: 'Other',
    importSubmit: 'Import transcript',
    importDone: 'Transcript imported.',
    importSkipped: '{{count}} malformed line(s) were skipped.',
    importEmpty: 'No usable segments were found in that transcript.',
  },
  meeting: {
    transcript: 'Transcript',
    noTranscript: 'No transcript yet.',
    provider: 'Provider',
    cost: 'Estimated cost',
    mapSpeakers: 'Name speakers',
    retranscribe: 'Re-transcribe',
    retranscribing: 'Queued for re-transcription.',
    edit: 'Edit',
    speakerDialog: 'Map speaker labels to names',
    processing: 'This meeting is still being processed. The page refreshes automatically.',
    failed: 'Processing failed',
    audioMissing: 'Audio is not available yet.',
    diagnostics: 'Upload diagnostics',
    diagnosticsEmpty: 'No client diagnostics for this meeting.',
    chunks: 'Uploaded pieces',
    chunksEmpty: 'No chunk records for this meeting.',
    chunkIndex: 'Piece',
    chunkAttempt: 'Attempt',
    chunkSize: 'Size',
    chunkUploaded: 'Uploaded',
    build: 'Build',
    lowConfidenceWordTitle: 'Low confidence — listen to check',
    checkMarkerTitle: 'Low-confidence line — needs a check',
    showOnlyCheck: 'Show only lines to check ({{count}})',
    lowConfidenceSummary: '{{n}} of {{m}} lines contain low-confidence words',
    confidenceLegend:
      'A dotted amber underline marks a word the engine is unsure about — click it to jump there and listen.',
    importedNoAudio:
      'This transcript was imported; no audio is attached. Upload the audio to enable playback and re-transcription.',
    importedNoAudioLink: 'Upload audio',
    retranscribeDisabledTitle: 'Re-transcribe needs assembled audio, which is not available yet.',
    version: 'Version',
    versionOption: 'v{{version}} · {{provider}}',
    tabs: 'Meeting sections',
    tab: {
      minutes: 'Minutes',
      transcript: 'Transcript',
      diagnostics: 'Diagnostics',
    },
    backToMinutes: '← Back to minutes',
    externalRef: 'Zoom meeting number',
    externalRefSaved: 'Meeting number saved.',
  },
  people: {
    open: 'Confirm people',
    title: 'Confirm who was on this call',
    hint: 'Display names on a call belong to accounts, not to voices. Give each speaker their real name, side and role, and add anyone who was present but never spoke. Once confirmed, the minutes name them outright instead of hedging.',
    name: 'Real name',
    side: 'Side',
    sides: { HOUSE: 'House', CLIENT: 'Client', OTHER: 'Other' },
    role: 'Role / title',
    rolePlaceholder: 'Designer — led the consultation',
    silent: 'Present but did not speak, or not audible',
    notInTranscript: 'Not in the transcript',
    addPerson: 'Add someone who did not speak',
    realClient: 'Client (the person the garment is for)',
    realClientPlaceholder: 'Nouf Bin Shouq (the bride)',
    preparedBy: 'Prepared by (overrides the house default)',
    preparedByPlaceholder: 'Leave empty to use the Account Manager on file',
    saved: 'People confirmed · {{n}} transcript line(s) relabelled.',
  },
  vocabulary: {
    title: 'Vocabulary',
    subtitle: 'Names and house terms sent to the transcription provider as key terms.',
    term: 'Term',
    category: 'Category',
    aliases: 'Aliases (comma separated)',
    add: 'Add term',
    remove: 'Remove',
    adminOnly: 'Only an administrator can change the vocabulary.',
  },
  momStatus: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    FINAL: 'Final',
    ARCHIVED: 'Archived',
  },
  mom: {
    title: 'Minutes',
    language: 'Document language',
    none: 'No minutes have been drafted for this meeting yet.',
    noContent: 'This version has no English copy.',
    noArabic: 'This version has no Arabic copy.',
    noKey: 'Generation is not configured on this server.',
    generate: 'Generate minutes',
    generating: 'Generating — this takes a minute or two.',
    drafting: 'Writing the minutes from the transcript…',
    regenerate: 'Regenerate with a note',
    regenerateHint: 'Say what to emphasise. It changes emphasis, never fidelity to the transcript.',
    regenerateCost: 'Regenerating costs roughly ${{cost}} — what the last run cost.',
    confirmPeopleHint:
      'Most of these are questions about who someone is. Confirm the people first, then regenerate: the minutes will name them outright.',
    instruction: 'Note for this run',
    failed: 'The minutes could not be written',
    reviewerNotes: 'Things to confirm before finalizing',
    cost: 'Cost',
    tokens: '{{input}} in / {{output}} out',
    version: 'Version',
    versionOption: 'v{{version}} · {{status}} · {{doc}}',
    saved: 'Saved.',
    forked: 'The final version was left untouched; your edits are in v{{version}}.',
    finalize: 'Finalize',
    finalizeConfirm:
      'This assigns the document number, locks this version, writes the action items and renders the PDFs. The number will be the next free one for this key ({{docNumber}}).',
    unsavedWarning: 'You have unsaved edits — save them first or they will not be in the final document.',
    finalized: 'Finalized as {{docNumber}} · {{n}} action item(s) written.',
    rerender: 'Re-render PDF',
    rendering: 'Rendering the PDFs…',
    downloadEn: 'PDF (EN)',
    downloadAr: 'PDF (AR)',
    preview: 'Preview',
    add: 'Add',
    remove: 'Remove',
    moveUp: 'Move up',
    moveDown: 'Move down',
    processStep: 'Process step (not said on the call)',
    uncited: 'No citation',
    chipTitle: 'Jump to this transcript line',
    owner: 'Owner',
    department: 'Dept',
    due: 'Due',
    status: 'Status',
    speaker: 'Speaker',
    quotesHint: 'Verbatim, in the language it was said in — the same text appears in both copies.',
    sections: {
      metadata: 'Record',
      attendees: 'Attendees & source',
      summary: 'Summary',
      clientAndOccasion: 'Client & occasion',
      designBrief: 'Design brief',
      discussionPoints: 'Discussion points',
      perDepartment: 'What this means for each department',
      decisions: 'Decisions & commitments',
      commitmentsToClient: 'Commitments made to the client',
      actions: 'Action items',
      openQuestions: 'Open questions & next steps',
      nextSteps: 'Next steps',
      inTheClientsWords: "In the client's words",
    },
    fields: {
      numberKey: 'Number key (surname in capitals)',
      mnNumber: 'Proposed MN number',
      date: 'Date',
      timeAndDuration: 'Time · duration',
      channelAndLanguage: 'Channel · language',
      client: 'Client',
      clientOrLeadId: 'Client / lead ID',
      dealId: 'Deal ID',
      meetingType: 'Meeting type',
      clientSide: 'Client side',
      houseSide: 'House side',
      alsoPresent: 'Also present',
      hostedBy: 'Hosted by',
      silentOrInaudible: 'Silent or inaudible',
      preparedBy: 'Prepared by',
      source: 'Source',
      occasion: 'Occasion',
      eventDate: 'Event date',
      wantedBy: 'Wanted by',
      budgetSignal: 'Budget signal',
      pieceUnderDiscussion: 'Piece under discussion',
      howSheReachedUs: 'How she reached us',
      wants: 'She wants',
      doesNotWant: 'She does not want',
      referencesAndTasteSignals: 'References & taste signals',
      imagePlaceholders: 'Image captions',
      department: 'Department',
      impactFromThisMeeting: 'Impact from this meeting',
      toConfirmBeforeQuoting: 'To confirm before quoting',
      acknowledgement: 'Print the client acknowledgement block',
    },
  },
  categories: {
    CLIENT: 'Client',
    STAFF: 'Staff',
    MATERIAL: 'Material',
    TECHNIQUE: 'Technique',
    PRODUCT: 'Product',
    OTHER: 'Other',
  },
};

const ar: typeof en = {
  brand: 'اجتماعات SL',
  nav: { meetings: 'الاجتماعات', new: 'اجتماع جديد', upload: 'رفع تسجيل', vocabulary: 'المصطلحات' },
  common: {
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    loading: 'جارٍ التحميل…',
    error: 'حدث خطأ',
    retry: 'إعادة المحاولة',
    signOut: 'تسجيل الخروج',
    none: '—',
    language: 'English',
    optional: 'اختياري',
  },
  login: {
    title: 'تسجيل الدخول',
    subtitle: 'شهيرة لاشين — سجل الاجتماعات',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    submit: 'دخول',
    failed: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
  },
  meetings: {
    title: 'الاجتماعات',
    empty: 'لا توجد اجتماعات بعد.',
    search: 'ابحث في العنوان أو العميل أو المكان',
    filterType: 'النوع',
    filterStatus: 'الحالة',
    from: 'من',
    to: 'إلى',
    all: 'الكل',
    duration: 'المدة',
    created: 'أنشأه',
  },
  types: {
    CLIENT: 'عميل',
    SUPPLIER: 'مورّد',
    INTERNAL: 'داخلي',
    MANAGEMENT: 'إدارة',
    ONLINE: 'أونلاين',
    GENERIC: 'عام',
  },
  statuses: {
    RECORDING: 'جارٍ التسجيل',
    UPLOADED: 'تم الرفع',
    TRANSCRIBING: 'جارٍ التفريغ',
    DRAFTING: 'إعداد المسودة',
    IN_REVIEW: 'قيد المراجعة',
    FINAL: 'نهائي',
    ARCHIVED: 'مؤرشف',
    FAILED: 'فشل',
    NO_SPEECH: 'لا يوجد كلام',
  },
  newMeeting: {
    title: 'اجتماع جديد',
    meetingTitle: 'العنوان',
    type: 'نوع الاجتماع',
    department: 'القسم',
    clientRef: 'مرجع العميل',
    leadRef: 'مرجع العميل المحتمل',
    location: 'المكان',
    language: 'لغة الاجتماع',
    attendees: 'الحضور',
    addAttendee: 'إضافة حاضر',
    attendeeName: 'الاسم',
    attendeeRole: 'الصفة في الاجتماع',
    create: 'إنشاء وبدء التسجيل',
    createUpload: 'إنشاء ورفع ملف',
    uploadSourceLabel: 'مصدر الرفع',
    uploadSourceFile: 'ملف',
    uploadSourceZoom: 'زووم',
  },
  recorder: {
    title: 'التسجيل',
    start: 'ابدأ التسجيل',
    stop: 'إيقاف ومعالجة',
    live: 'جارٍ التسجيل',
    idle: 'جاهز',
    uploaded: 'المقاطع المرفوعة',
    pending: 'في انتظار الرفع',
    failed: 'مقاطع فشل رفعها',
    retrying: 'ما زالت المحاولة جارية',
    stalled: 'رفع أحد المقاطع يستغرق وقتاً طويلاً. المحاولة مستمرة — اترك الصفحة مفتوحة.',
    captureLost: 'توقف الميكروفون عن إرسال الصوت. تحقق من الجهاز ثم أوقف التسجيل وأنهِ الرفع.',
    recorderError: 'توقف المسجل بشكل غير متوقع.',
    stopFailed: 'لم يكتمل',
    stopFailedHelp: 'التسجيل محفوظ على الخادم. أعد إنهاء الرفع — لا تبدأ تسجيلاً جديداً.',
    retryFinish: 'إعادة إنهاء الرفع',
    resume: 'استئناف الرفع',
    resumeAvailable: 'يوجد تسجيل غير مكتمل ({{count}} مقطع) محفوظ على هذا الجهاز.',
    offline: 'لا يوجد اتصال — المقاطع محفوظة وسترفع عند عودة الاتصال.',
    micDenied: 'تم رفض إذن الميكروفون.',
    unsupported: 'هذا المتصفح لا يدعم التسجيل الصوتي.',
    wakeLock: 'الشاشة مفعّلة',
    finishing: 'جارٍ إنهاء الرفع…',
    uploadingPiece: 'جارٍ رفع المقطع {{current}} من {{total}}',
    lastError: 'آخر خطأ',
    done: 'تم تسليم التسجيل للمعالجة.',
    keepOpen: 'اترك هذه الصفحة مفتوحة حتى ينتهي الرفع.',
    micCheck: 'فحص الميكروفون',
    micLabel: 'الميكروفون: {{label}}',
    micLabelUnknown: 'ميكروفون الهاتف',
    micLevelAria: 'مستوى دخل الميكروفون',
    startMicCheck: 'بدء فحص الميكروفون',
    interruptedBody:
      'يوجد تسجيل سابق لهذا الاجتماع توقف قبل الضغط على إيقاف ({{count}} مقاطع، حوالي {{seconds}} ثانية، موجودة على الخادم). ابدأ من جديد لحذفه والتسجيل مرة أخرى، أو أنهِ التسجيل بما تم رفعه.',
    interruptedStartOver: 'ابدأ من جديد',
    interruptedFinish: 'إنهاء بما تم رفعه',
    micUnavailable:
      'تعذّر فتح الميكروفون. اسمح لهذا الموقع باستخدام الميكروفون من إعدادات المتصفح، وتأكد من عدم استخدامه في تطبيق آخر، ثم أعد تحميل الصفحة.',
    selectMic: 'الميكروفون',
    silenceWarning:
      'لا يصل أي صوت إلى الميكروفون. تأكد من أن الهاتف غير متصل كميكروفون بجهاز آخر، وأنه لا يوجد تطبيق آخر يستخدم الميكروفون.',
  },
  upload: {
    title: 'رفع تسجيل',
    pick: 'اختر ملف صوت أو فيديو',
    drop: 'أفلت الملف هنا أو اضغط للاختيار',
    submit: 'رفع',
    uploading: 'جارٍ الرفع',
    done: 'تم الرفع. تم إدراج التفريغ في قائمة المعالجة.',
    tooBig: 'حجم الملف أكبر من ٥٠٠ ميجابايت.',
    importTitle: 'استيراد نص مفرّغ جاهز',
    importIntro:
      'الصق نصاً بصيغة WebVTT ‏(.vtt)، أو نصاً عادياً، أو ملف JSON للنص المفرّغ، وتجاوز خطوة التفريغ تماماً.',
    importPasteLabel: 'الصق نص التفريغ',
    importPastePlaceholder: '‎WEBVTT… أو [00:00:00] المتحدث: النص… أو JSON',
    importFileLabel: 'أو اختر ملفاً (.vtt، ‎.txt، ‎.json)',
    importProviderLabel: 'المصدر',
    importProviderZoom: 'زووم',
    importProviderOther: 'آخر',
    importSubmit: 'استيراد النص',
    importDone: 'تم استيراد النص المفرّغ.',
    importSkipped: 'تم تجاهل {{count}} سطر غير صالح.',
    importEmpty: 'لم يتم العثور على مقاطع صالحة في هذا النص.',
  },
  meeting: {
    transcript: 'النص',
    noTranscript: 'لا يوجد نص بعد.',
    provider: 'المزوّد',
    cost: 'التكلفة التقديرية',
    mapSpeakers: 'تسمية المتحدثين',
    retranscribe: 'إعادة التفريغ',
    retranscribing: 'تم إدراج إعادة التفريغ.',
    edit: 'تعديل',
    speakerDialog: 'اربط تسميات المتحدثين بالأسماء',
    processing: 'الاجتماع قيد المعالجة. تُحدَّث الصفحة تلقائياً.',
    failed: 'فشلت المعالجة',
    audioMissing: 'الصوت غير متاح بعد.',
    diagnostics: 'تشخيص الرفع',
    diagnosticsEmpty: 'لا توجد سجلات تشخيص لهذا الاجتماع.',
    chunks: 'المقاطع المرفوعة',
    chunksEmpty: 'لا توجد سجلات مقاطع لهذا الاجتماع.',
    chunkIndex: 'المقطع',
    chunkAttempt: 'المحاولة',
    chunkSize: 'الحجم',
    chunkUploaded: 'وقت الرفع',
    build: 'الإصدار',
    lowConfidenceWordTitle: 'ثقة منخفضة — استمع للتحقق',
    checkMarkerTitle: 'سطر بثقة منخفضة — يحتاج مراجعة',
    showOnlyCheck: 'عرض الأسطر التي تحتاج مراجعة فقط ({{count}})',
    lowConfidenceSummary: '{{n}} من {{m}} أسطر تحتوي كلمات بثقة منخفضة',
    confidenceLegend:
      'الخط المنقط الكهرماني تحت كلمة يعني أن المحرك غير متأكد منها — انقر عليها للاستماع والتحقق.',
    importedNoAudio:
      'تم استيراد هذا النص المفرّغ؛ لا يوجد صوت مرفق. ارفع الصوت لتفعيل التشغيل وإعادة التفريغ.',
    importedNoAudioLink: 'رفع الصوت',
    retranscribeDisabledTitle: 'إعادة التفريغ تحتاج إلى صوت مجمّع، وهو غير متاح حالياً.',
    version: 'الإصدار',
    versionOption: 'الإصدار {{version}} · {{provider}}',
    tabs: 'أقسام الاجتماع',
    tab: {
      minutes: 'المحضر',
      transcript: 'التفريغ',
      diagnostics: 'التشخيص',
    },
    backToMinutes: '→ العودة إلى المحضر',
    externalRef: 'رقم اجتماع Zoom',
    externalRefSaved: 'تم حفظ رقم الاجتماع.',
  },
  people: {
    open: 'تأكيد الحضور',
    title: 'تأكيد من حضر هذا الاتصال',
    hint: 'أسماء العرض في الاتصال تخصّ الحسابات لا الأصوات. أعطِ كل متحدث اسمه الحقيقي وجهته ودوره، وأضف من كان حاضراً ولم يتكلم. بعد التأكيد يذكر المحضر الأسماء صراحةً بلا تحفّظ.',
    name: 'الاسم الحقيقي',
    side: 'الجهة',
    sides: { HOUSE: 'الدار', CLIENT: 'العميلة', OTHER: 'أخرى' },
    role: 'الدور / الصفة',
    rolePlaceholder: 'المصمِّمة — قادت الاستشارة',
    silent: 'حاضر ولم يتكلم، أو صوته غير مسموع',
    notInTranscript: 'غير موجود في التفريغ',
    addPerson: 'إضافة شخص لم يتكلم',
    realClient: 'العميلة (صاحبة الفستان)',
    realClientPlaceholder: 'نوف بن شوق (العروس)',
    preparedBy: 'الإعداد (يتجاوز الافتراضي)',
    preparedByPlaceholder: 'اتركه فارغاً لاستخدام مديرة حسابات العملاء المسجَّلة',
    saved: 'تم تأكيد الحضور · أُعيدت تسمية {{n}} سطراً في التفريغ.',
  },
  vocabulary: {
    title: 'المصطلحات',
    subtitle: 'الأسماء ومصطلحات الدار التي تُرسل لمزوّد التفريغ كمفردات مفتاحية.',
    term: 'المصطلح',
    category: 'التصنيف',
    aliases: 'مرادفات (مفصولة بفواصل)',
    add: 'إضافة مصطلح',
    remove: 'حذف',
    adminOnly: 'التعديل متاح للمسؤول فقط.',
  },
  momStatus: {
    DRAFT: 'مسودة',
    IN_REVIEW: 'قيد المراجعة',
    FINAL: 'نهائي',
    ARCHIVED: 'مؤرشف',
  },
  mom: {
    title: 'المحضر',
    language: 'لغة المستند',
    none: 'لم تُكتب بعد محاضر لهذا الاجتماع.',
    noContent: 'لا توجد نسخة إنجليزية لهذا الإصدار.',
    noArabic: 'لا توجد نسخة عربية لهذا الإصدار.',
    noKey: 'خدمة التوليد غير مهيأة على هذا الخادم.',
    generate: 'توليد المحضر',
    generating: 'جارٍ التوليد — يستغرق دقيقة أو دقيقتين.',
    drafting: 'جارٍ كتابة المحضر من التفريغ…',
    regenerate: 'إعادة التوليد مع ملاحظة',
    regenerateHint: 'اكتب ما يجب التركيز عليه. الملاحظة تغيّر التركيز فقط، لا الأمانة للتفريغ.',
    regenerateCost: 'إعادة التوليد تكلّف نحو {{cost}}$ — وهي تكلفة المحاولة الأخيرة.',
    confirmPeopleHint:
      'معظم هذه الملاحظات أسئلة عن هوية الأشخاص. أكّد الحضور أولاً ثم أعد التوليد: سيذكر المحضر أسماءهم صراحةً.',
    instruction: 'ملاحظة لهذه المحاولة',
    failed: 'تعذّرت كتابة المحضر',
    reviewerNotes: 'ما يجب تأكيده قبل الاعتماد',
    cost: 'التكلفة',
    tokens: '{{input}} داخل / {{output}} خارج',
    version: 'الإصدار',
    versionOption: 'إصدار {{version}} · {{status}} · {{doc}}',
    saved: 'تم الحفظ.',
    forked: 'لم يُمسّ الإصدار النهائي؛ تعديلاتك في الإصدار {{version}}.',
    finalize: 'اعتماد',
    finalizeConfirm:
      'سيُخصَّص رقم المستند، ويُقفل هذا الإصدار، وتُكتب بنود العمل، وتُصدَّر ملفات PDF. الرقم سيكون التالي المتاح لهذا المفتاح ({{docNumber}}).',
    unsavedWarning: 'لديك تعديلات غير محفوظة — احفظها أولاً وإلا لن تظهر في المستند النهائي.',
    finalized: 'تم الاعتماد برقم {{docNumber}} · كُتب {{n}} بند عمل.',
    rerender: 'إعادة إصدار PDF',
    rendering: 'جارٍ إصدار ملفات PDF…',
    downloadEn: 'PDF (إنجليزي)',
    downloadAr: 'PDF (عربي)',
    preview: 'معاينة',
    add: 'إضافة',
    remove: 'حذف',
    moveUp: 'لأعلى',
    moveDown: 'لأسفل',
    processStep: 'خطوة إجرائية (لم تُذكر في الاتصال)',
    uncited: 'بلا مرجع',
    chipTitle: 'الانتقال إلى هذا السطر في التفريغ',
    owner: 'المسؤول',
    department: 'القسم',
    due: 'الاستحقاق',
    status: 'الحالة',
    speaker: 'المتحدث',
    quotesHint: 'نصاً، باللغة التي قيلت بها — النص نفسه يظهر في النسختين.',
    sections: {
      metadata: 'السجل',
      attendees: 'الحضور والمصدر',
      summary: 'الملخص',
      clientAndOccasion: 'العميلة والمناسبة',
      designBrief: 'الموجز التصميمي',
      discussionPoints: 'نقاط النقاش',
      perDepartment: 'ما يعنيه هذا الاجتماع لكل قسم',
      decisions: 'القرارات والالتزامات',
      commitmentsToClient: 'الالتزامات تجاه العميلة',
      actions: 'بنود العمل',
      openQuestions: 'أسئلة مفتوحة والخطوات التالية',
      nextSteps: 'الخطوات التالية',
      inTheClientsWords: 'بكلمات العميلة',
    },
    fields: {
      numberKey: 'مفتاح الترقيم (اسم العائلة بحروف كبيرة)',
      mnNumber: 'رقم المحضر المقترح',
      date: 'التاريخ',
      timeAndDuration: 'الوقت · المدة',
      channelAndLanguage: 'القناة · اللغة',
      client: 'العميلة',
      clientOrLeadId: 'رقم العميلة / العميلة المحتملة',
      dealId: 'رقم الصفقة',
      meetingType: 'نوع الاجتماع',
      clientSide: 'جهة العميلة',
      houseSide: 'الدار',
      alsoPresent: 'حضر أيضاً',
      hostedBy: 'استضافة الاتصال',
      silentOrInaudible: 'لم يُسمع صوته',
      preparedBy: 'الإعداد',
      source: 'المصدر',
      occasion: 'المناسبة',
      eventDate: 'تاريخ المناسبة',
      wantedBy: 'موعد التسليم المطلوب',
      budgetSignal: 'مؤشر الميزانية',
      pieceUnderDiscussion: 'القطعة محل النقاش',
      howSheReachedUs: 'طريقة التواصل',
      wants: 'ما تريده',
      doesNotWant: 'ما لا تريده',
      referencesAndTasteSignals: 'المراجع ومؤشرات الذوق',
      imagePlaceholders: 'تعليقات الصور',
      department: 'القسم',
      impactFromThisMeeting: 'الأثر من هذا الاجتماع',
      toConfirmBeforeQuoting: 'ما يجب تأكيده قبل التسعير',
      acknowledgement: 'طباعة خانة إقرار العميلة',
    },
  },
  categories: {
    CLIENT: 'عميل',
    STAFF: 'فريق',
    MATERIAL: 'خامة',
    TECHNIQUE: 'تقنية',
    PRODUCT: 'منتج',
    OTHER: 'أخرى',
  },
};

const STORAGE_KEY = 'sl-meetings-lang';

export function storedLanguage(): Language {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'ar' || value === 'en') return value;
  } catch {
    /* private mode */
  }
  return 'en';
}

export function applyDirection(lang: Language): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private mode */
  }
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, ar: { translation: ar } },
  lng: storedLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

applyDirection(storedLanguage());

export default i18n;
